'use client';

import React from 'react';
import ProgramOverview from './ProgramOverview';
import ProgramTimeline from './ProgramTimeline';
import ProgramComponents from './ProgramComponents';
import ParticipantProfile from './ParticipantProfile';
import ExpectedOutcomes from './ExpectedOutcomes';
import InvestmentModel from './InvestmentModel';
import CaseStudyFramework from './CaseStudyFramework';
import ReadinessAssessment from './ReadinessAssessment';

const FlagshipProgramInteractive: React.FC = () => {
  const heroData = {
    title: "Institutional Strategy & Leadership Capacity Program",
    subtitle: "Integrated Strategy Development & Leadership Capability Building",
    duration: "6-12 Month Engagement",
    description: "A comprehensive advisory program that combines strategic framework development with leadership capacity building and implementation architecture. Designed for institutions committed to substantial transformation and prepared for sustained engagement."
  };

  const overviewPoints = [
    {
      icon: "LightBulbIcon",
      title: "Strategic Framework Development",
      description: "Develop clear institutional strategy with governance structures, reform roadmaps, and execution frameworks aligned to organizational realities and political constraints."
    },
    {
      icon: "UserGroupIcon",
      title: "Leadership Capacity Building",
      description: "Build executive decision-making capabilities, strategic thinking skills, and organizational leadership capacity through structured development programs."
    },
    {
      icon: "CogIcon",
      title: "Implementation Architecture",
      description: "Establish execution systems, performance frameworks, and change management structures that enable sustained institutional transformation."
    }
  ];

  const timelinePhases = [
    {
      phase: "Phase 1",
      duration: "Weeks 1-8",
      title: "Diagnostic & Design",
      description: "Comprehensive institutional assessment, stakeholder engagement, and strategic framework design. Establish baseline capabilities and identify transformation priorities.",
      deliverables: [
        "Institutional diagnostic report with strategic priorities",
        "Stakeholder analysis and engagement framework",
        "Strategic framework design with governance structures",
        "Leadership capability assessment baseline",
        "Implementation roadmap with phased approach"
      ]
    },
    {
      phase: "Phase 2",
      duration: "Weeks 9-20",
      title: "Development & Capacity Building",
      description: "Strategic framework refinement, leadership development programs, and execution system design. Build organizational capabilities while developing implementation architecture.",
      deliverables: [
        "Refined strategic framework with execution plans",
        "Leadership development program delivery",
        "Performance management system design",
        "Change management framework and tools",
        "Pilot implementation in selected areas"
      ]
    },
    {
      phase: "Phase 3",
      duration: "Weeks 21-36",
      title: "Implementation & Embedding",
      description: "Full-scale implementation support, ongoing leadership coaching, and organizational capability embedding. Ensure systematic execution and address emerging challenges.",
      deliverables: [
        "Implementation support across priority areas",
        "Executive coaching and leadership development",
        "Performance monitoring and adjustment systems",
        "Organizational capability embedding programs",
        "Mid-term review and course correction"
      ]
    },
    {
      phase: "Phase 4",
      duration: "Weeks 37-48",
      title: "Sustainability & Transition",
      description: "Ensure sustainable institutional capabilities, transition to internal ownership, and establish continuous improvement systems. Prepare for post-engagement independence.",
      deliverables: [
        "Sustainability framework and internal ownership transition",
        "Continuous improvement systems and processes",
        "Final capability assessment and progress evaluation",
        "Knowledge transfer and documentation",
        "Post-engagement support framework"
      ]
    }
  ];

  const programComponents = [
    {
      icon: "DocumentTextIcon",
      title: "Strategic Framework Development",
      description: "Comprehensive strategy development that addresses institutional governance, reform priorities, and execution realities. Frameworks are designed for practical implementation rather than theoretical elegance.",
      elements: [
        "Institutional strategy and governance design",
        "Reform roadmap with political feasibility analysis",
        "Execution framework aligned to organizational capacity",
        "Stakeholder engagement and communication strategy",
        "Performance measurement and accountability systems",
        "Risk management and mitigation frameworks"
      ]
    },
    {
      icon: "AcademicCapIcon",
      title: "Leadership Capacity Building",
      description: "Structured leadership development that builds executive decision-making capabilities, strategic thinking skills, and organizational leadership capacity through systematic programs.",
      elements: [
        "Executive strategic thinking development",
        "Decision-making frameworks and tools",
        "Organizational leadership capability building",
        "AI literacy and responsible technology adoption",
        "Change leadership and stakeholder management",
        "Performance coaching and capability assessment"
      ]
    },
    {
      icon: "WrenchScrewdriverIcon",
      title: "Implementation Architecture",
      description: "Execution systems and organizational structures that enable sustained transformation. Focus on building internal capabilities for ongoing implementation beyond the engagement period.",
      elements: [
        "PMO advisory and execution support systems",
        "Performance management and monitoring frameworks",
        "Change management structures and processes",
        "Organizational capability embedding programs",
        "Continuous improvement systems",
        "Knowledge transfer and sustainability planning"
      ]
    }
  ];

  const participantCriteria = [
    {
      icon: "BuildingOffice2Icon",
      title: "Institutional Mandate",
      description: "Government ministries, state-owned enterprises, regulatory authorities, or development programs with clear transformation mandates and senior leadership commitment."
    },
    {
      icon: "UserCircleIcon",
      title: "Leadership Engagement",
      description: "Senior executive participation in strategic sessions, willingness to address institutional constraints, and commitment to building organizational capabilities."
    },
    {
      icon: "ClockIcon",
      title: "Time Commitment",
      description: "Sustained engagement over 6-12 months with dedicated internal coordination resources and access to organizational data and stakeholders."
    },
    {
      icon: "CurrencyDollarIcon",
      title: "Resource Allocation",
      description: "Budget allocation for comprehensive engagement including strategy development, leadership programs, and implementation support throughout the program duration."
    }
  ];

  const expectedOutcomes = [
    {
      category: "Strategic Clarity",
      outcomes: [
        "Clear institutional strategy with governance structures",
        "Reform roadmap aligned to organizational realities",
        "Execution framework with accountability systems",
        "Stakeholder engagement and communication strategy",
        "Performance measurement and monitoring systems"
      ]
    },
    {
      category: "Leadership Capability",
      outcomes: [
        "Enhanced executive strategic thinking and decision-making",
        "Improved organizational leadership capabilities",
        "AI literacy and responsible technology adoption",
        "Change leadership and stakeholder management skills",
        "Sustainable performance coaching culture"
      ]
    },
    {
      category: "Execution Readiness",
      outcomes: [
        "Functional PMO and execution support systems",
        "Embedded performance management frameworks",
        "Change management structures and processes",
        "Organizational capability for sustained transformation",
        "Continuous improvement systems and culture"
      ]
    }
  ];

  const investmentFeatures = [
    {
      icon: "UserIcon",
      title: "Senior Advisory Team Engagement",
      description: "Direct engagement with Diaax senior advisors throughout the program, ensuring strategic continuity, institutional understanding, and experienced partnership at the executive level."
    },
    {
      icon: "ChartBarSquareIcon",
      title: "Systematic Methodology",
      description: "Proven four-phase approach that ensures strategic clarity, capability building, and execution readiness through structured progression and measurable milestones."
    },
    {
      icon: "ClockIcon",
      title: "Sustained Partnership",
      description: "6-12 month engagement duration allows for deep institutional understanding, meaningful capability building, and sustainable transformation rather than superficial interventions."
    },
    {
      icon: "ShieldCheckIcon",
      title: "Long-term Value Focus",
      description: "Investment structured to deliver sustained institutional capabilities that continue generating value long after formal engagement concludes."
    }
  ];

  const caseStudies = [
    {
      institutionType: "Government Ministry",
      challenge: "Ministry required comprehensive digital transformation strategy while building internal leadership capacity to execute complex reform agenda across multiple departments.",
      approach: "Integrated 9-month program combining strategic framework development with executive leadership programs and PMO advisory support for phased implementation.",
      outcome: "Clear digital transformation roadmap with governance structures, enhanced leadership decision-making capabilities, and functional execution systems enabling sustained reform progress."
    },
    {
      institutionType: "State-Owned Enterprise",
      challenge: "SOE needed strategic repositioning for competitive market while developing organizational leadership capabilities and establishing performance management systems.",
      approach: "12-month engagement integrating strategic framework design with leadership capacity building programs and implementation architecture for organizational transformation.",
      outcome: "Refined strategic direction with execution frameworks, improved leadership capabilities across management levels, and embedded performance systems supporting ongoing transformation."
    },
    {
      institutionType: "Development Program",
      challenge: "Multi-year development program required strategic clarity on implementation priorities while building institutional capacity for sustained program delivery and impact measurement.",
      approach: "8-month program combining strategic framework development with capacity building initiatives and performance monitoring system design for program effectiveness.",
      outcome: "Clear implementation strategy with prioritized initiatives, enhanced institutional delivery capabilities, and functional monitoring systems enabling evidence-based program management."
    }
  ];

  const readinessCriteria = [
    {
      category: "Strategic Readiness",
      requirements: [
        "Clear institutional mandate for transformation",
        "Senior leadership commitment to strategic process",
        "Willingness to address institutional constraints",
        "Openness to systematic methodology and long-term focus",
        "Realistic expectations about transformation timelines"
      ]
    },
    {
      category: "Organizational Capacity",
      requirements: [
        "Dedicated internal coordination resources",
        "Access to organizational data and stakeholders",
        "Ability to allocate time for leadership participation",
        "Internal communication and change management capability",
        "Budget allocation for sustained engagement"
      ]
    },
    {
      category: "Implementation Commitment",
      requirements: [
        "Commitment to implementation beyond planning",
        "Willingness to build internal execution capabilities",
        "Support for organizational change and adaptation",
        "Openness to performance measurement and accountability",
        "Long-term perspective on institutional transformation"
      ]
    },
    {
      category: "Partnership Alignment",
      requirements: [
        "Alignment with senior advisory engagement model",
        "Appreciation for systematic methodology approach",
        "Commitment to ethical and responsible practices",
        "Openness to capacity building and knowledge transfer",
        "Realistic understanding of transformation complexity"
      ]
    }
  ];

  return (
    <>
      <ProgramOverview overviewPoints={overviewPoints} />
      <ProgramTimeline phases={timelinePhases} />
      <ProgramComponents components={programComponents} />
      <ParticipantProfile criteria={participantCriteria} />
      <ExpectedOutcomes outcomes={expectedOutcomes} />
      <InvestmentModel features={investmentFeatures} />
      <CaseStudyFramework caseStudies={caseStudies} />
      <ReadinessAssessment criteria={readinessCriteria} />
    </>
  );
};

export default FlagshipProgramInteractive;